import { useState, useRef, useEffect } from "react";
import { Search, User, Phone, Clock, CheckCircle, AlertTriangle, RefreshCw, Keyboard, Users } from "lucide-react";
import { publicApi } from "../api/client";
import type { Recipient } from "../types";

export default function Kiosk() {
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [recipient, setRecipient] = useState<Recipient | null>(null);
  const [showKeyboard, setShowKeyboard] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input on load
  useEffect(() => {
    document.title = "Semakan Insentif Mahasiswa Anak Johor 2026 (IMAJ)";
    inputRef.current?.focus();
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setIsLoading(true);
    setError("");
    setRecipient(null);

    try {
      const result = await publicApi.searchRecipient(query.trim());
      if (result.error) {
        setError(result.error);
      } else if (result.recipient) {
        setRecipient(result.recipient);
      }
    } catch (err) {
      setError("Ralat berlaku. Sila cuba lagi.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setQuery("");
    setRecipient(null);
    setError("");
    inputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex items-center max-w-4xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">B</span>
            </div>
            <div>
              <h1 className="text-xl font-semibold text-white">Semakan Insentif Mahasiswa Anak Johor 2026 (IMAJ)</h1>
              <p className="text-sm text-slate-400">Peringkat Dun Permas dan Dun Johor Jaya</p>
            </div>
          </div>
        </div>
      </header>

      {/* Banner Image */}
      <div className="flex justify-center bg-slate-900 border-b border-slate-700 px-4 py-4">
        <img
          src="/banner.jpg"
          alt="Majlis Insentif Mahasiswa Anak Johor (IMAJ)"
          className="w-full max-w-sm rounded-xl object-contain"
        />
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Search Form */}
        <form onSubmit={handleSearch} className="w-full max-w-2xl mb-8">
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="No. IC..."
              className="w-full px-6 py-5 text-2xl border-2 border-slate-600 bg-slate-800 text-white rounded-2xl focus:ring-4 focus:ring-blue-500/30 focus:border-blue-500 outline-none placeholder-slate-500"
              autoComplete="off"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-2">
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="p-2 text-slate-400 hover:text-white"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowKeyboard(!showKeyboard)}
                className="p-2 text-slate-400 hover:text-white"
              >
                <Keyboard className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="w-full mt-4 py-4 bg-blue-600 text-white text-xl font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-3"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-6 h-6 animate-spin" />
                Mencari...
              </>
            ) : (
              <>
                <Search className="w-6 h-6" />
                Cari Penerima
              </>
            )}
          </button>
        </form>

        {/* Virtual Keyboard */}
        {showKeyboard && (
          <div className="w-full max-w-2xl mb-6">
            <SimpleKeyboard onKeyPress={(key) => {
              setQuery(prev => prev + key);
              inputRef.current?.focus();
            }} onBackspace={() => {
              setQuery(prev => prev.slice(0, -1));
              inputRef.current?.focus();
            }} onClear={() => {
              setQuery("");
              inputRef.current?.focus();
            }} />
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="w-full max-w-2xl bg-red-900/50 border border-red-700 rounded-2xl p-6 mb-6 flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-red-400 mt-1" />
            <div>
              <p className="text-xl text-red-200">{error}</p>
              <button
                onClick={handleReset}
                className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Cuba Lagi
              </button>
            </div>
          </div>
        )}

        {/* Result */}
        {recipient && (
          <div className="w-full max-w-2xl">
            {/* Status Banner */}
            <div className={`rounded-2xl p-6 mb-6 ${recipient.statusKehadiran === 1 ? "bg-green-900/50 border-2 border-green-600" : "bg-amber-900/50 border-2 border-amber-500"}`}>
              <div className="flex items-center gap-4">
                {recipient.statusKehadiran === 1 ? (
                  <>
                    <CheckCircle className="w-12 h-12 text-green-400" />
                    <div>
                      <p className="text-2xl font-bold text-green-200">
                        {recipient.wakilNama ? "Sudah Daftar Masuk (WAKIL)" : "Sudah Daftar Masuk (SENDIRI)"}
                      </p>
                      <p className="text-green-300">
                        {recipient.checkedInAt && new Date(recipient.checkedInAt).toLocaleString("ms-MY", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Clock className="w-12 h-12 text-amber-400" />
                    <div>
                      <p className="text-2xl font-bold text-amber-200">Belum Daftar Masuk</p>
                      <p className="text-amber-300">Status semakan semasa: belum hadir.</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* WAKIL Details - Show if checked in by WAKIL */}
            {recipient.statusKehadiran === 1 && recipient.wakilNama && (
              <div className="rounded-2xl p-6 mb-6 bg-amber-900/50 border-2 border-amber-500">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-8 h-8 text-amber-400" />
                  <p className="text-xl font-bold text-amber-200">Maklumat WAKIL</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-amber-300">Nama</p>
                    <p className="text-lg font-semibold text-white">{recipient.wakilNama}</p>
                  </div>
                  <div>
                    <p className="text-sm text-amber-300">No. IC</p>
                    <p className="text-lg font-semibold text-white font-mono">{recipient.wakilNoIc}</p>
                  </div>
                  {recipient.wakilNoTel && (
                    <div>
                      <p className="text-sm text-amber-300">No. Telefon</p>
                      <p className="text-lg font-semibold text-white">{recipient.wakilNoTel}</p>
                    </div>
                  )}
                  {recipient.wakilHubungan && (
                    <div>
                      <p className="text-sm text-amber-300">Hubungan</p>
                      <p className="text-lg font-semibold text-white">{recipient.wakilHubungan}</p>
                    </div>
                  )}
                </div>
                {recipient.wakilAlamat && (
                  <div className="mt-4">
                    <p className="text-sm text-amber-300">Alamat</p>
                    <p className="text-lg font-semibold text-white">{recipient.wakilAlamat}</p>
                  </div>
                )}
              </div>
            )}

            {/* Details Grid */}
            <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
              {/* Top Section - Bil, Slot Masa, DUN, Umur, Institusi */}
              <div className="bg-slate-700/50 rounded-xl p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-600/70">
                    <p className="text-sm text-slate-400 mb-1">Bil (Rujukan)</p>
                    <p className="text-4xl md:text-5xl font-extrabold text-blue-300 font-mono tracking-wide leading-none">
                      {recipient.bil}
                    </p>
                  </div>
                  <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-600/70">
                    <p className="text-sm text-slate-400 mb-1">Slot Masa</p>
                    <p className="text-xl font-semibold text-white">{recipient.slotMasa || "-"}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-600/70">
                    <p className="text-sm text-slate-400 mb-1">DUN / Kawasan</p>
                    <p className="text-xl font-semibold text-white">{recipient.dun}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-600/70">
                    <p className="text-sm text-slate-400 mb-1">Umur</p>
                    <p className="text-xl font-semibold text-white">{recipient.age ?? "-"}</p>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-600/70">
                  <p className="text-sm text-slate-400 mb-1">Institusi / Universiti</p>
                  <p className="text-2xl font-bold text-blue-400 break-words">
                    {recipient.institution || "-"}
                  </p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-4 p-4 bg-slate-700/30 rounded-xl">
                  <User className="w-6 h-6 text-slate-400" />
                  <div className="min-w-0">
                    <p className="text-sm text-slate-400">Nama</p>
                    <p className="text-xl font-semibold text-white break-words whitespace-normal leading-snug">{recipient.nama}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-slate-700/30 rounded-xl">
                  <span className="text-sm text-slate-400 font-mono">IC</span>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-400">No. IC</p>
                    <p className="text-xl font-semibold text-white font-mono break-all">{recipient.noIc}</p>
                  </div>
                </div>
                {recipient.noTelefon && (
                  <div className="flex items-center gap-4 p-4 bg-slate-700/30 rounded-xl">
                    <Phone className="w-6 h-6 text-slate-400" />
                    <div className="min-w-0">
                      <p className="text-sm text-slate-400">No. Telefon</p>
                      <p className="text-xl font-semibold text-white break-all">{recipient.noTelefon}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={handleReset}
                className="w-full py-4 bg-slate-700 text-white text-xl font-semibold rounded-xl hover:bg-slate-600 transition flex items-center justify-center gap-3"
              >
                <RefreshCw className="w-6 h-6" />
                Semak Lagi
              </button>
            </div>
          </div>
        )}
      </main>

      <footer className="px-6 py-4 text-center text-sm text-slate-500 border-t border-slate-800">
        Portal Bantuan Mahasiswa DUN Permas & Johor Jaya
      </footer>
    </div>
  );
}

// Simple Virtual Keyboard
function SimpleKeyboard({ 
  onKeyPress, 
  onBackspace, 
  onClear 
}: { 
  onKeyPress: (key: string) => void;
  onBackspace: () => void;
  onClear: () => void;
}) {
  const rows = [
    ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
    ["Z", "X", "C", "V", "B", "N", "M"],
  ];

  return (
    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
      {rows.map((row, i) => (
        <div key={i} className="flex justify-center gap-2 mb-2">
          {row.map((key) => (
            <button
              key={key}
              onClick={() => onKeyPress(key)}
              className="w-10 h-12 bg-slate-700 text-white text-lg font-semibold rounded-lg hover:bg-slate-600 transition"
            >
              {key}
            </button>
          ))}
          {i === 3 && (
            <>
              <button
                onClick={onBackspace}
                className="px-4 h-12 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition"
              >
                ←
              </button>
              <button
                onClick={onClear}
                className="px-4 h-12 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 transition"
              >
                Clear
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
